import React, { useState, useEffect, createContext, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * Toast Notification System
 * Inspired by ReactBits.dev notification patterns
 * 
 * Features:
 * - Multiple toast types (success, error, warning, info)
 * - Auto-dismiss with progress bar
 * - Stack multiple toasts
 * - Animations
 * - Position control
 */

// Toast Context
const ToastContext = createContext();

// Toast Provider Component
export const ToastProvider = ({ children, position = 'top-right', maxToasts = 5 }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = (message, type = 'info', duration = 5000, options = {}) => {
    const id = Date.now() + Math.random();
    const newToast = {
      id,
      message,
      type,
      duration,
      ...options
    };

    setToasts((prevToasts) => {
      const updatedToasts = [...prevToasts, newToast];
      // Limit to maxToasts
      return updatedToasts.slice(-maxToasts);
    });

    // Auto-dismiss if duration is set
    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }

    return id;
  };

  const removeToast = (id) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  };

  const removeAllToasts = () => {
    setToasts([]);
  };

  // Helper functions
  const success = (message, options) => addToast(message, 'success', 5000, options);
  const error = (message, options) => addToast(message, 'error', 7000, options);
  const warning = (message, options) => addToast(message, 'warning', 5000, options);
  const info = (message, options) => addToast(message, 'info', 5000, options);

  const value = {
    toasts,
    addToast,
    removeToast,
    removeAllToasts,
    success,
    error,
    warning,
    info
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} position={position} removeToast={removeToast} />
    </ToastContext.Provider>
  );
};

// Toast Container Component
const ToastContainer = ({ toasts, position, removeToast }) => {
  const positionClasses = {
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
    'top-center': 'top-4 left-1/2 transform -translate-x-1/2',
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'bottom-center': 'bottom-4 left-1/2 transform -translate-x-1/2'
  };

  return (
    <div className={`fixed ${positionClasses[position]} z-70 space-y-2 max-w-md w-full px-4`} role="region" aria-label="Notifications">
      <AnimatePresence>
        {toasts.map((toast) => (
          <Toast key={toast.id} toast={toast} onClose={() => removeToast(toast.id)} />
        ))}
      </AnimatePresence>
    </div>
  );
};

// Individual Toast Component
const Toast = ({ toast, onClose }) => {
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    if (toast.duration <= 0) return;

    const interval = setInterval(() => {
      setProgress((prev) => {
        const newProgress = prev - (100 / (toast.duration / 100));
        if (newProgress <= 0) {
          clearInterval(interval);
          return 0;
        }
        return newProgress;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [toast.duration]);

  const icons = {
    success: 'fas fa-check-circle',
    error: 'fas fa-exclamation-circle',
    warning: 'fas fa-exclamation-triangle',
    info: 'fas fa-info-circle'
  };

  const colors = {
    success: {
      bg: 'bg-green-50',
      border: 'border-green-500',
      text: 'text-green-800',
      icon: 'text-green-500',
      progress: 'bg-green-500'
    },
    error: {
      bg: 'bg-red-50',
      border: 'border-red-500',
      text: 'text-red-800',
      icon: 'text-red-500',
      progress: 'bg-red-500'
    },
    warning: {
      bg: 'bg-yellow-50',
      border: 'border-yellow-500',
      text: 'text-yellow-800',
      icon: 'text-yellow-500',
      progress: 'bg-yellow-500'
    },
    info: {
      bg: 'bg-blue-50',
      border: 'border-blue-500',
      text: 'text-blue-800',
      icon: 'text-blue-500',
      progress: 'bg-blue-500'
    }
  };

  const color = colors[toast.type] || colors.info;

  return (
    <motion.div
      initial={{ opacity: 0, y: -20, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, x: 100, scale: 0.9 }}
      transition={{ duration: 0.2 }}
      className={`${color.bg} ${color.border} border-l-4 rounded-lg shadow-lg overflow-hidden`}
    >
      <div className="p-4 flex items-start gap-3">
        <div className={`${color.icon} text-xl flex-shrink-0 mt-0.5`}>
          <i className={icons[toast.type]}></i>
        </div>
        
        <div className="flex-1 min-w-0">
          {toast.title && (
            <h4 className={`font-semibold ${color.text} mb-1`}>
              {toast.title}
            </h4>
          )}
          <p className={`${color.text} text-sm`}>
            {toast.message}
          </p>
        </div>

        <button
          onClick={onClose}
          className={`${color.text} hover:opacity-70 transition-opacity flex-shrink-0`}
          aria-label="Dismiss notification"
        >
          <i className="fas fa-times"></i>
        </button>
      </div>

      {/* Progress Bar */}
      {toast.duration > 0 && (
        <div className="h-1 bg-gray-200">
          <motion.div
            className={`h-full ${color.progress}`}
            initial={{ width: '100%' }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.1, ease: 'linear' }}
          />
        </div>
      )}
    </motion.div>
  );
};

// Custom Hook to use Toast
export const useToast = () => {
  const context = useContext(ToastContext);
  
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  
  return context;
};

export default Toast;

